/* GENERATED from microlab (mobile) src/modules/anatomyRecall — keep in sync; edit the mobile .ts source and regenerate. */
/*
  RecallProgressService — per-user Anatomy Recall progress + spaced repetition.

  Persistence follows the existing 3D anatomy quiz (anatomyModelQuiz/storage.ts):
  a versioned JSON document in AsyncStorage keyed by user id. There is no
  server endpoint for anatomy-quiz progress today, so none is introduced here.

  Mastery is driven ONLY by independent recall (Recall / Master stages,
  first attempt, no hint, no reveal). Guided Trace answers never count.
*/
import { getAsyncStorageItem, setAsyncStorageItem } from './storage';
import { cardById, isCardAvailable, RECALL_CARDS } from './cards';
const PREFIX = '@microlab/anatomy-recall-v1_';
const DAY = 24 * 60 * 60 * 1000;

/** Spaced-repetition ladder (days). */
export const REVIEW_LADDER_DAYS = [1, 3, 7, 14, 30];
function storageKey(userId) {
  return `${PREFIX}${userId || 'guest'}`;
}
export function emptyRecallStore() {
  return {
    version: 1,
    cards: {}
  };
}
export async function loadRecallStore(userId) {
  try {
    const raw = await getAsyncStorageItem(storageKey(userId));
    if (!raw) return emptyRecallStore();
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1 || typeof parsed.cards !== 'object') return emptyRecallStore();
    return {
      version: 1,
      cards: parsed.cards || {}
    };
  } catch {
    return emptyRecallStore();
  }
}
export async function saveRecallStore(userId, store) {
  try {
    await setAsyncStorageItem(storageKey(userId), JSON.stringify(store));
  } catch {
    /* storage full / unavailable — progress is best-effort */
  }
}
function emptyStructure(key) {
  return {
    key,
    traced: false,
    correct: 0,
    incorrect: 0,
    hints: 0,
    reveals: 0,
    streak: 0,
    box: -1,
    lastPracticedAt: 0,
    nextReviewAt: 0
  };
}
function emptyCard(cardId) {
  return {
    cardId,
    stage: 'trace',
    traceCompleted: false,
    recallCompleted: false,
    masterCompleted: false,
    lastPracticedAt: 0,
    structures: {}
  };
}

/* ───────────── recording answers (pure) ───────────── */

export function recordAnswer(store, event) {
  const now = event.now ?? Date.now();
  const card = store.cards[event.cardId] || emptyCard(event.cardId);
  const prev = card.structures[event.key] || emptyStructure(event.key);
  const next = {
    ...prev,
    hints: prev.hints + event.hintsUsed,
    reveals: prev.reveals + (event.revealed ? 1 : 0),
    incorrect: prev.incorrect + event.wrongAttempts,
    lastPracticedAt: now
  };
  if (event.stage === 'trace') {
    // Guided typing: learned, never mastered, no schedule change.
    next.traced = true;
  } else if (event.independent) {
    next.correct += 1;
    next.streak += 1;
    // Climb the ladder when the review was due, or while still in the
    // initial learning steps (first two boxes) of a new structure.
    if (now >= prev.nextReviewAt || prev.box < 1) {
      next.box = Math.min(prev.box + 1, REVIEW_LADDER_DAYS.length - 1);
    }
    next.nextReviewAt = now + REVIEW_LADDER_DAYS[Math.max(0, next.box)] * DAY;
  } else {
    // Needed retries, a hint or a reveal → shorten the interval.
    next.streak = 0;
    next.box = Math.max(-1, prev.box - (event.revealed || event.wrongAttempts ? 2 : 1));
    next.nextReviewAt = event.revealed || event.wrongAttempts ? now : now + DAY / 2;
    if (event.correct && event.hintsUsed === 0 && !event.revealed) next.correct += 1;
  }
  return {
    ...store,
    cards: {
      ...store.cards,
      [event.cardId]: {
        ...card,
        lastPracticedAt: now,
        structures: {
          ...card.structures,
          [event.key]: next
        }
      }
    }
  };
}
export function recordStageComplete(store, cardId, stage) {
  const card = store.cards[cardId] || emptyCard(cardId);
  const nextStage = stage === 'trace' ? 'recall' : stage === 'recall' ? 'master' : 'master';
  return {
    ...store,
    cards: {
      ...store.cards,
      [cardId]: {
        ...card,
        stage: nextStage,
        traceCompleted: card.traceCompleted || stage === 'trace',
        recallCompleted: card.recallCompleted || stage === 'recall',
        masterCompleted: card.masterCompleted || stage === 'master',
        lastPracticedAt: Date.now()
      }
    }
  };
}

/* ───────────── derived metrics ───────────── */

export function isStructureMastered(s) {
  return Boolean(s && s.box >= 1 && s.streak >= 2);
}
export function isStructureLearned(s) {
  return Boolean(s && s.box >= 0);
}
export function needsReview(s, now = Date.now()) {
  if (!s) return false;
  if (s.box < 0) return s.incorrect > 0 || s.reveals > 0;
  return s.nextReviewAt <= now;
}
function structureScore(s) {
  if (!s) return 0;
  if (isStructureMastered(s)) return 1;
  if (isStructureLearned(s)) return 0.5 + Math.min(0.3, s.streak * 0.15);
  if (s.traced) return 0.1;
  return 0;
}
export function summarizeCard(card, store, now = Date.now()) {
  const progress = store.cards[card.id];
  const total = card.items.length;
  const structures = card.items.map(entry => progress?.structures[entry.key]);
  const mastered = structures.filter(isStructureMastered).length;
  const learned = structures.filter(isStructureLearned).length;
  const due = structures.filter(s => needsReview(s, now)).length;
  const mastery = total ? Math.round(structures.reduce((sum, s) => sum + structureScore(s), 0) / total * 100) : 0;
  const started = Boolean(progress && Object.keys(progress.structures).length);
  let status = 'notStarted';
  if (started) status = 'learning';
  if (progress?.traceCompleted) status = 'practicing';
  if (total && mastered === total) status = 'mastered';
  const unlocked = {
    trace: true,
    recall: Boolean(progress?.traceCompleted),
    master: Boolean(progress?.recallCompleted)
  };
  const nextStage = !unlocked.recall ? 'trace' : !unlocked.master ? 'recall' : 'master';
  return {
    status,
    mastery,
    learned,
    mastered,
    due,
    total,
    nextStage,
    unlocked
  };
}
/** Unique-structure totals across all playable cards. */
export function summarizeOverall(store, now = Date.now()) {
  const playable = RECALL_CARDS.filter(isCardAvailable);
  const best = new Map();
  playable.forEach(card => {
    const progress = store.cards[card.id];
    card.items.forEach(entry => {
      const s = progress?.structures[entry.key];
      const current = best.get(entry.key);
      if (!best.has(entry.key) || structureScore(s) > structureScore(current)) {
        best.set(entry.key, s);
      }
    });
  });
  const values = [...best.values()];
  const total = values.length;
  return {
    cardsCompleted: playable.filter(card => summarizeCard(card, store, now).status === 'mastered').length,
    cardsAvailable: playable.length,
    structuresLearned: values.filter(isStructureLearned).length,
    structuresMastered: values.filter(isStructureMastered).length,
    structuresDue: values.filter(s => needsReview(s, now)).length,
    structuresTotal: total,
    percent: total ? Math.round(values.reduce((sum, s) => sum + structureScore(s), 0) / total * 100) : 0
  };
}
export function summarizeRegion(regionId, store) {
  const cards = RECALL_CARDS.filter(card => card.region === regionId);
  const playable = cards.filter(isCardAvailable);
  const summaries = playable.map(card => summarizeCard(card, store));
  const structures = playable.reduce((sum, card) => sum + card.items.length, 0);
  const weighted = summaries.reduce((sum, s) => sum + s.mastery * s.total, 0);
  return {
    cards: cards.length,
    playable: playable.length,
    structures,
    mastery: structures ? Math.round(weighted / structures) : 0,
    due: summaries.reduce((sum, s) => sum + s.due, 0)
  };
}

/** Master queue priority: weakest / due / previously missed first. */
export function masterPriority(cardId, key, store, now = Date.now()) {
  const s = cardById(cardId) ? store.cards[cardId]?.structures[key] : undefined;
  if (!s) return 2;
  let score = 0;
  if (needsReview(s, now)) score += 3;
  score += Math.min(3, s.incorrect + s.reveals);
  score += Math.max(0, 2 - s.box);
  return score;
}
